import { Component, Inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { ICellRendererParams } from '@ag-grid-community/core';
import { ShareClass } from '@models';
import { stringCompare } from '@utils/sort-utils';

export interface LitProperties {
  pdfDownloadLink: string;
  dctermsType: string;
  imageUrl: string;
  fileExtension: string;
  shareclass: {
    previewLink: string;
    shareClassName?: string;
    shareClassCode?: string;
  };
}

export interface LitAgGridCellParams extends ICellRendererParams {
  data: {
    shareClass: string[];
    literatureData: { [key: string]: LitProperties };
    product?: { shareClasses: ShareClass[] };
  };
  isPreviewLink: boolean;
  isSiteIntl: boolean;
  showOnlyIcon: boolean;
  colDef: { colId: string };
}
@Component({
  selector: 'ft-icon-column',
  templateUrl: './icon-column.component.html',
  styleUrls: ['./icon-column.component.scss'],
})
export class IconColumnComponent {
  /**
   * Params
   */
  public params: LitAgGridCellParams;
  public isSiteIntl;
  public thumbnailAvailable;
  public targetLink;
  public isCustom: boolean;
  public isDocumentAvailable = false;
  public regulatoryDocumentFileExtension: string;
  public showOnlyIcon: boolean;
  public isPreviewLink = false;

  constructor(@Inject(WINDOW) readonly windowRef: Window) {}

  agInit(params: LitAgGridCellParams) {
    this.params = params;
    this.thumbnailAvailable = params?.data?.literatureData?.imageUrl
      ? true
      : false;
    this.isSiteIntl = params.isSiteIntl;
    this.showOnlyIcon = params.showOnlyIcon;
    this.isCustom = params.data?.shareClass ? true : false;
    if (this.isCustom) {
      this.isPreviewLink = params.isPreviewLink;
      this.isDocumentAvailable =
        params.colDef.colId in params.data.literatureData &&
        params.data.literatureData[params.colDef.colId].pdfDownloadLink
          ?.length > 0;
      this.regulatoryDocumentFileExtension = params.data.literatureData[
        params.colDef.colId
      ]?.fileExtension
        ? params.data.literatureData[params.colDef.colId].fileExtension
        : 'pdf';
    }
  }

  openDocument(params: LitAgGridCellParams) {
    if (params.colDef.colId in params.data.literatureData) {
      const documentLink = this.getDocumentLink(params);
      this.windowRef.open(documentLink, '_blank');
    }
  }

  /**
   * returns download link or preview link if document is configured in literature site config preview
   * @param params ag-grid configuration with literature data and config
   */
  getDocumentLink(params: LitAgGridCellParams): string {
    const literatureDocument: LitProperties =
      params.data.literatureData[params.colDef.colId];
    let documentLink = literatureDocument.pdfDownloadLink;

    if (this.isPreviewLink) {
      let documentSuffix: string = params.colDef.colId.toLowerCase();
      if (documentSuffix === 'q1-holding') {
        documentSuffix = 'fiscal-q1-holdings';
      }
      if (documentSuffix === 'q3-holding') {
        documentSuffix = 'fiscal-q3-holdings';
      }

      // handle link formation for single share class row in custom listing
      if (
        params.data.shareClass.length === 1 &&
        params.data.shareClass[0] !== '-' &&
        params.data.shareClass[0] !==
          literatureDocument.shareclass.shareClassName
      ) {
        let currentShareClassCode: string;
        params.data.product?.shareClasses.forEach((data) => {
          if (data.shareClassName === params.data.shareClass[0]) {
            currentShareClassCode = data.shareClassCode;
          }
        });
        documentLink =
          this.getReplaceShareClassCode(
            literatureDocument.shareclass.previewLink,
            literatureDocument.shareclass.shareClassCode,
            currentShareClassCode
          ) +
          '#' +
          documentSuffix;
      } else {
        documentLink =
          literatureDocument.shareclass.previewLink + '#' + documentSuffix;
      }
    }
    return documentLink;
  }

  getReplaceShareClassCode(
    previewLink: string,
    oldShareClassCode: string,
    newShareClassCode: string
  ): string {
    return previewLink.replace(
      '/' + oldShareClassCode,
      '/' + newShareClassCode
    );
  }
}
